import * as R from 'ramda';
import {
  ApiReducer,
  makeApiAction,
  makeApiReducer,
  RsaaTypes,
} from '../../../../core/redux/api/reduxStandardApiAction';
import { IAppState } from '../../../../core/redux/IAppState';
import { apiGetRedemptionEntitlements } from '../../../../core/redux/api/endpoints';
import { ACTION_TYPE } from '../../../../core/redux/actionType';
import { addError, addLoading, getLoading, removeLoadingAndError } from '../../../../core/redux/api/module';
import { hasTrippedBreaker, updateBreaker } from '../../../../core/redux/api/circuitBreaker/module';
import { updateGuest } from '../../../module';
import { Entitlement } from '../../models';
import { normalizeEntitlements } from '../../module';


export const KEY_ACTION = ACTION_TYPE.GET_DTW_ENTITLEMENTS_REQUEST;
export const ACTIONS    = [
  ACTION_TYPE.GET_DTW_ENTITLEMENTS_REQUEST,
  ACTION_TYPE.GET_DTW_ENTITLEMENTS_RESPONSE,
  ACTION_TYPE.GET_DTW_ENTITLEMENTS_ERROR,
];


/**
 * Creates an action that can be dispatched to fetch a guest's Direct to Window (as opposed to direct to lift, or
 * Friends and Family) entitlements. DTW entitlements are how some resorts who don't support RFID passes give guests
 * access to the mountain. A response will include details like the guest's DTW redemption history.
 */
export const getDtwEntitlementsAction = (guestId: string) => makeApiAction(
  {
    method:   'GET',
    headers:  (state: IAppState) => ({
      'content-type':  'application/json',
      'Authorization': `Bearer ${state.auth.bearerToken}`,
    }),
    endpoint: state => apiGetRedemptionEntitlements(
      guestId,
      state.resortToolsConfig.dtwCategoryId,
      state.resortToolsConfig.enableRedemptionDisplayOnly,
      state.selectedResort.resortAbbreviation,
      false,
      ),
    types:    ACTIONS.map(action => ({ type: action, meta: { guestId } })) as RsaaTypes,
    bailout:  (state: IAppState) => !!getLoading(state, guestId, KEY_ACTION) || hasTrippedBreaker(state, KEY_ACTION),
  },
);


const reducer: ApiReducer<Entitlement[]> = (state, action, error, isLoading, payload) => {
  const guestId = action.meta.guestId;

  if (isLoading) {
    return addLoading(state, guestId, KEY_ACTION);
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  if (error) {
    return addError(withBreaker, guestId, KEY_ACTION, error);
  }

  const noLoadingOrError = removeLoadingAndError(withBreaker, guestId, KEY_ACTION);

  return updateGuest(noLoadingOrError, guestId, R.assocPath([ 'directToWindow' ], normalizeEntitlements(payload)));
};


export const getDtwEntitlementsReducer = makeApiReducer<Entitlement[]>(ACTIONS, KEY_ACTION, reducer);
